import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, X, MessageCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;

// Lightweight in-app chat — option (b) from the chat scope discussion.
// Polls the messages endpoint every 5s, no WebSockets. Auth required: if the
// visitor isn't logged in we show a hint instead of the composer.
export default function TeamChatDialog({ open, onClose, recipient, recipientName, recipientAvatar }) {
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const isAuthed = Boolean(token);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` }, withCredentials: true }), [token]);

  const fetchMessages = useCallback(async () => {
    if (!recipient || !isAuthed) return;
    try {
      const { data } = await axios.get(`${API}/team/messages?with=${encodeURIComponent(recipient)}`, auth());
      setMessages(data.messages || []);
      setMe(data.me);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load messages');
    }
  }, [recipient, isAuthed, auth]);

  // Initial load + polling
  useEffect(() => {
    if (!open || !isAuthed) return;
    setLoading(true);
    fetchMessages().finally(() => setLoading(false));
    const t = setInterval(fetchMessages, POLL_MS);
    return () => clearInterval(t);
  }, [open, isAuthed, fetchMessages]);

  // Auto-scroll to latest message whenever the list changes
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = body.trim();
    if (!text || !isAuthed) return;
    setSending(true);
    try {
      await axios.post(`${API}/team/messages`, { recipient, body: text }, auth());
      setBody('');
      await fetchMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="team-chat-dialog"
        className="w-[95vw] max-w-md p-0 overflow-hidden"
        style={{ backgroundColor: '#F4F1EB' }}
      >
        {/* Visually hidden title/description for screen readers — keeps the
            Radix a11y contract satisfied while we render our own custom header. */}
        <DialogTitle className="sr-only">Chat with {recipientName || recipient}</DialogTitle>
        <DialogDescription className="sr-only">
          In-app team messaging. Auto-refreshes every 5 seconds.
        </DialogDescription>
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ backgroundColor: '#1A2E2E', borderColor: 'rgba(255,255,255,0.1)' }}>
          {recipientAvatar ? (
            <img src={recipientAvatar} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1F4FBE', color: 'white' }}>
              <MessageCircle className="w-4 h-4" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate" style={{ color: 'white', fontFamily: 'Manrope' }}>{recipientName || recipient}</p>
            <p className="text-[10px]" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>In-app team chat · auto-refreshes every 5s</p>
          </div>
          <button onClick={() => onClose(false)} className="p-1.5 rounded-full hover:bg-white/10" data-testid="team-chat-close">
            <X className="w-4 h-4" style={{ color: 'white' }} />
          </button>
        </div>

        {/* Body */}
        {!isAuthed ? (
          <div className="p-8 text-center">
            <MessageCircle className="w-8 h-8 mx-auto mb-2" style={{ color: '#A3D4D4' }} />
            <p className="text-sm font-bold mb-1" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>Sign in to chat</p>
            <p className="text-xs" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
              Only logged-in team members can use the in-app chat. Visitors can use the WhatsApp button instead.
            </p>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="px-4 py-3 h-80 overflow-y-auto space-y-2" style={{ backgroundColor: '#F4F1EB' }}>
              {loading && messages.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#7A8C8C' }} />
                </div>
              )}
              {!loading && messages.length === 0 && (
                <p className="text-xs italic text-center py-6" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  No messages yet. Send the first one below.
                </p>
              )}
              {messages.map((m) => {
                const mine = (m.sender || '').toLowerCase() === (me || '').toLowerCase();
                const time = new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.id}`}>
                    <div
                      className="max-w-[78%] px-3 py-2 rounded-2xl"
                      style={{
                        backgroundColor: mine ? '#1F4FBE' : 'white',
                        color: mine ? 'white' : '#1A2E2E',
                        borderBottomRightRadius: mine ? '4px' : '16px',
                        borderBottomLeftRadius: mine ? '16px' : '4px',
                        boxShadow: mine ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
                      }}
                    >
                      <p className="text-sm leading-snug whitespace-pre-wrap break-words" style={{ fontFamily: 'Manrope' }}>{m.body}</p>
                      <p className="text-[9px] mt-1 opacity-70 text-right" style={{ fontFamily: 'Manrope' }}>{time}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Composer */}
            <div className="px-3 py-3 flex items-end gap-2 border-t" style={{ backgroundColor: 'white', borderColor: '#EAE5D9' }}>
              <textarea
                data-testid="chat-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Type a message…"
                rows={1}
                className="flex-1 px-3 py-2 rounded-xl border text-sm outline-none resize-none max-h-32"
                style={{ borderColor: '#A3D4D4', fontFamily: 'Manrope', backgroundColor: '#F4F1EB' }}
              />
              <button
                onClick={send}
                disabled={sending || !body.trim()}
                data-testid="chat-send"
                className="p-2.5 rounded-xl text-white disabled:opacity-50 transition-all"
                style={{ backgroundColor: '#1F4FBE' }}
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            {error && (
              <p className="text-[11px] px-4 pb-2" style={{ color: '#FF6F59', fontFamily: 'Manrope' }}>{error}</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
